import { Suspense, useEffect } from "react";
import { useRouter, usePathname } from "@/lib/navigation";
import { useAuth } from "@/hooks/useAuth";
import { PermissionDenied } from "@/components/PermissionDenied";
import { AGENT_STATEMENTS_ENABLED } from "@/lib/commissionAccess";

const GODADDY_ALLOWED_PATHS = ["/poynt-collect", "/helpdesk"];

// sessionStorage key written by /force-password-reset when the user clicks
// "Skip for now". The value is the grace deadline as an ISO string; we honour
// the skip only while the deadline is still in the future. The key dies with
// the tab, so re-login + close/reopen both force the prompt again.
const SKIP_KEY = "pwd_reset_skipped";

function shouldHonorForcedResetSkip(graceUntilIso: string | null): boolean {
  if (!graceUntilIso) return false;
  if (typeof window === "undefined") return false;
  const stored = (() => {
    try {
      return window.sessionStorage.getItem(SKIP_KEY);
    } catch {
      return null;
    }
  })();
  if (!stored) return false;
  const skipDeadline = new Date(stored);
  if (Number.isNaN(skipDeadline.getTime())) return false;
  return skipDeadline.getTime() > Date.now();
}

/**
 * Route access policy. Each entry restricts a path prefix to INTERNAL plus the
 * listed extra user types. Anything not listed is open to all authenticated
 * users. Non-internal users (including an INTERNAL admin while IMPERSONATING an
 * agent) who hit a restricted route get the PermissionDenied screen — the
 * backstop for the sidebar hiding these items.
 */
const RESTRICTED_ROUTES: { prefix: string; allow: string[] }[] = [
  { prefix: "/admin", allow: [] }, // INTERNAL only — incl. /admin/users (no agent sub-agent mgmt)
  { prefix: "/ai-call-log", allow: [] },
  { prefix: "/ai-call-log", allow: [] },
  { prefix: "/internal-tools", allow: [] },
  { prefix: "/mailroom", allow: [] }, // INTERNAL only — email templates, sending, logs
  // Agents reach only their own statements (/commissions/:reportId), and only once those ship.
  { prefix: "/commissions", allow: AGENT_STATEMENTS_ENABLED ? ["AGENT"] : [] },
  { prefix: "/residuals", allow: [] },
  { prefix: "/reporting", allow: [] },
  { prefix: "/poynt-collect", allow: ["GODADDY_MERCHANT"] },
];

function isRouteDenied(pathname: string, userType: string | undefined): boolean {
  if (!userType || userType === "INTERNAL") return false;
  for (const r of RESTRICTED_ROUTES) {
    const matches = pathname === r.prefix || pathname.startsWith(`${r.prefix}/`);
    if (matches && !r.allow.includes(userType)) return true;
  }
  return false;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRouteContent({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  // Access is evaluated against the EFFECTIVE user — the impersonated user when
  // impersonating, otherwise the real one — so impersonation faithfully shows
  // what that user can (and cannot) reach.
  const effectiveUser = user?.impersonating ?? user;
  const effectiveType = effectiveUser?.userType;

  useEffect(() => {
    if (loading) return;

    if (!user) {
      const returnTo = pathname ? `?returnTo=${encodeURIComponent(pathname)}` : "";
      router.replace(`/login${returnTo}`);
      return;
    }

    // Forced password reset trumps everything else. Send the user to the
    // interstitial unless the skip is still valid (grace deadline in the
    // future + sessionStorage flag set). Impersonation is intentionally
    // exempt — an INTERNAL admin viewing the app as another user shouldn't
    // have to reset that user's password.
    if (
      user.mustResetPassword &&
      !user.impersonating &&
      pathname !== "/force-password-reset"
    ) {
      const skip = shouldHonorForcedResetSkip(user.tempPasswordGraceUntil);
      if (!skip) {
        const returnTo = pathname
          ? `?returnTo=${encodeURIComponent(pathname)}`
          : "";
        router.replace(`/force-password-reset${returnTo}`);
        return;
      }
    }

    // GoDaddy merchants only see /poynt-collect and /helpdesk.
    if (effectiveType === "GODADDY_MERCHANT") {
      const allowed = GODADDY_ALLOWED_PATHS.some(
        (p) => pathname === p || pathname.startsWith(`${p}/`),
      );
      if (!allowed) router.replace("/poynt-collect");
    }
  }, [loading, user, effectiveType, pathname, router]);

  if (loading || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // GoDaddy is redirected (above); agents/sub-agents see an explicit denial.
  if (effectiveType !== "GODADDY_MERCHANT" && isRouteDenied(pathname, effectiveType)) {
    return <PermissionDenied />;
  }

  return <>{children}</>;
}

export default function ProtectedRoute(props: ProtectedRouteProps) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      }
    >
      <ProtectedRouteContent {...props} />
    </Suspense>
  );
}
